import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Stack,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  DragIndicator as DragIndicatorIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Flag as FlagIcon
} from '@mui/icons-material';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { taskService } from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Helper to parse date strings as local dates (avoid UTC shifts)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  
  // Handle YYYY-MM-DD string explicitly as local date
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    // Set to noon (12:00) to avoid timezone shifts/midnight issues
    return new Date(year, month - 1, day, 12, 0, 0);
  }
  
  return new Date(dateStr);
};

const STATUS_COLORS = {
  not_started: '#757575',
  in_progress: '#2196f3',
  completed: '#4caf50',
  blocked: '#f44336'
};

const PRIORITY_COLORS = {
  low: '#9e9e9e',
  medium: '#ff9800',
  high: '#ff5722',
  urgent: '#d32f2f'
};

function GanttChartDragDrop({ tasks, onCreateTask, onEditTask, onTasksChange, milestones = [], onEditMilestone, onCreateMilestone }) {
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [zoom, setZoom] = useState(1);
  const [localTasks, setLocalTasks] = useState(tasks);

  // Drag state
  const [dragState, setDragState] = useState({
    isDragging: false,
    taskId: null,
    startX: 0,
    originalStart: null,
    originalEnd: null,
    resizeMode: null // null, 'start', 'end'
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportingPDF, setExportingPDF] = useState(false);

  const timelineRef = useRef(null);
  const ganttContainerRef = useRef(null);

  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  // Calculate date range
  const dateRange = useMemo(() => {
    let start, end;

    if (viewMode === 'month') {
      start = startOfMonth(currentDate);
      end = endOfMonth(currentDate);
    } else if (viewMode === 'week') {
      start = addDays(currentDate, -currentDate.getDay());
      end = addDays(start, 6);
    } else {
      const month = currentDate.getMonth();
      const quarterStart = Math.floor(month / 3) * 3;
      start = new Date(currentDate.getFullYear(), quarterStart, 1);
      end = endOfMonth(new Date(currentDate.getFullYear(), quarterStart + 2, 1));
    }

    return { start, end };
  }, [currentDate, viewMode]);

  const timelineDays = useMemo(() => {
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  }, [dateRange]);

  const visibleTasks = useMemo(() => {
    return localTasks.filter(task => {
      if (!task.start_date && !task.end_date) return false;
      const taskStart = parseLocalDate(task.start_date) || dateRange.start;
      const taskEnd = parseLocalDate(task.end_date) || dateRange.end;
      return taskEnd >= dateRange.start && taskStart <= dateRange.end;
    });
  }, [localTasks, dateRange]);

  const unscheduledTasks = useMemo(() => {
    return localTasks.filter(task => !task.start_date && !task.end_date);
  }, [localTasks]);

  const visibleMilestones = useMemo(() => {
    return milestones.filter(m => {
      const d = parseLocalDate(m.due_date);
      return d && d >= dateRange.start && d <= dateRange.end;
    });
  }, [milestones, dateRange]);

  const getMilestonePosition = (milestone) => {
    const d = parseLocalDate(milestone.due_date);
    const totalDays = differenceInDays(dateRange.end, dateRange.start) + 1;
    const offset = differenceInDays(d, dateRange.start);
    return `${(offset / totalDays) * 100}%`;
  };

  const dayWidth = useMemo(() => {
    const base = viewMode === 'week' ? 80 : viewMode === 'month' ? 40 : 20;
    return base * zoom;
  }, [viewMode, zoom]);

  const getTaskBarStyle = (task) => {
    const taskStart = parseLocalDate(task.start_date) || dateRange.start;
    const taskEnd = parseLocalDate(task.end_date) || dateRange.end;

    const visibleStart = taskStart < dateRange.start ? dateRange.start : taskStart;
    const visibleEnd = taskEnd > dateRange.end ? dateRange.end : taskEnd;

    const totalDays = differenceInDays(dateRange.end, dateRange.start) + 1;
    const startOffset = differenceInDays(visibleStart, dateRange.start);
    const duration = differenceInDays(visibleEnd, visibleStart) + 1;

    const leftPercent = (startOffset / totalDays) * 100;
    const widthPercent = (duration / totalDays) * 100;

    return {
      left: `${leftPercent}%`,
      width: `${Math.max(widthPercent, 1)}%`,
      backgroundColor: STATUS_COLORS[task.status] || STATUS_COLORS.not_started
    };
  };

  const handleMouseDown = (e, task, resizeMode = null) => {
    e.stopPropagation();

    setDragState({
      isDragging: true,
      taskId: task.id,
      startX: e.clientX,
      originalStart: parseLocalDate(task.start_date),
      originalEnd: parseLocalDate(task.end_date),
      resizeMode
    });

    document.body.style.cursor = resizeMode ? 'ew-resize' : 'move';
  };

  const handleMouseMove = (e) => {
    if (!dragState.isDragging || !timelineRef.current) return;

    const deltaX = e.clientX - dragState.startX;
    // Use actual dayWidth (takes zoom into account) instead of container width
    const pixelsPerDay = dayWidth;
    const daysDelta = Math.round(deltaX / pixelsPerDay);

    if (daysDelta === 0) return;

    const task = localTasks.find(t => t.id === dragState.taskId);
    if (!task) return;

    let newStartDate = dragState.originalStart;
    let newEndDate = dragState.originalEnd;

    if (dragState.resizeMode === 'start') {
      // Resize start date
      if (newStartDate) {
        newStartDate = addDays(dragState.originalStart, daysDelta);
        // Can't move start after end
        if (newEndDate && newStartDate > newEndDate) {
          newStartDate = newEndDate;
        }
      }
    } else if (dragState.resizeMode === 'end') {
      // Resize end date
      if (newEndDate) {
        newEndDate = addDays(dragState.originalEnd, daysDelta);
        // Can't move end before start
        if (newStartDate && newEndDate < newStartDate) {
          newEndDate = newStartDate;
        }
      }
    } else {
      // Move both dates
      if (newStartDate) {
        newStartDate = addDays(dragState.originalStart, daysDelta);
      }
      if (newEndDate) {
        newEndDate = addDays(dragState.originalEnd, daysDelta);
      }
    }

    // Update local state for immediate visual feedback
    setLocalTasks(prev => prev.map(t => {
      if (t.id === dragState.taskId) {
        return {
          ...t,
          start_date: newStartDate ? format(newStartDate, 'yyyy-MM-dd') : t.start_date,
          end_date: newEndDate ? format(newEndDate, 'yyyy-MM-dd') : t.end_date
        };
      }
      return t;
    }));
  };

  const handleMouseUp = async () => {
    if (!dragState.isDragging) return;

    document.body.style.cursor = 'default';

    const task = localTasks.find(t => t.id === dragState.taskId);
    if (!task) {
      setDragState({ isDragging: false, taskId: null, startX: 0, originalStart: null, originalEnd: null, resizeMode: null });
      return;
    }

    // Check if dates actually changed
    const originalStartStr = dragState.originalStart ? format(dragState.originalStart, 'yyyy-MM-dd') : null;
    const originalEndStr = dragState.originalEnd ? format(dragState.originalEnd, 'yyyy-MM-dd') : null;

    if (task.start_date === originalStartStr && task.end_date === originalEndStr) {
      setDragState({ isDragging: false, taskId: null, startX: 0, originalStart: null, originalEnd: null, resizeMode: null });
      return;
    }

    // Update task via API
    try {
      await taskService.update(task.id, {
        start_date: task.start_date,
        end_date: task.end_date
      });

      setSnackbar({
        open: true,
        message: 'Dates mises à jour avec succès',
        severity: 'success'
      });

      // Notify parent component
      if (onTasksChange) {
        onTasksChange();
      }
    } catch (error) {
      console.error('Error updating task dates:', error);

      // Revert to original dates on error
      setLocalTasks(prev => prev.map(t => {
        if (t.id === dragState.taskId) {
          return {
            ...t,
            start_date: originalStartStr,
            end_date: originalEndStr
          };
        }
        return t;
      }));

      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour des dates',
        severity: 'error'
      });
    }

    setDragState({ isDragging: false, taskId: null, startX: 0, originalStart: null, originalEnd: null, resizeMode: null });
  };

  useEffect(() => {
    if (dragState.isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState.isDragging, dragState.startX, dragState.taskId]);

  const handlePrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => isSameDay(date, new Date());

  const handleExportPDF = async () => {
    if (!ganttContainerRef.current || exportingPDF) return;

    try {
      setExportingPDF(true);
      setSnackbar({
        open: true,
        message: 'Génération du PDF en cours...',
        severity: 'info'
      });

      // Capture the Gantt chart as an image
      const canvas = await html2canvas(ganttContainerRef.current, {
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Calculate PDF dimensions
      const imgWidth = 297; // A4 landscape width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF
      const pdf = new jsPDF({
        orientation: imgHeight > 210 ? 'portrait' : 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Add title
      pdf.setFontSize(16);
      pdf.text('Diagramme de Gantt', 14, 15);

      // Add date
      pdf.setFontSize(10);
      pdf.text(`Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })}`, 14, 22);

      // Add image
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 10, 28, imgWidth - 20, imgHeight - 20);

      // Generate filename
      const fileName = `gantt_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.pdf`;

      // Download PDF
      pdf.save(fileName);

      setSnackbar({
        open: true,
        message: 'PDF exporté avec succès !',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          justifyContent="space-between" 
          alignItems="center" 
          spacing={2}
        >
          {/* Left: Navigation */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
            <Button variant="outlined" size="small" onClick={handleToday}>
              Aujourd'hui
            </Button>
            <IconButton size="small" onClick={handlePrevious}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton size="small" onClick={handleNext}>
              <ChevronRightIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
              {format(currentDate, 'MMMM yyyy', { locale: fr })}
            </Typography>
          </Box>

          {/* Center: View Modes */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={viewMode === 'week' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('week')}
            >
              Semaine
            </Button>
            <Button
              variant={viewMode === 'month' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('month')}
            >
              Mois
            </Button>
            <Button
              variant={viewMode === 'quarter' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('quarter')}
            >
              Trimestre
            </Button>
          </Box>

          {/* Right: Tools */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
                disabled={zoom <= 0.5}
              >
                <ZoomOutIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setZoom(Math.min(2, zoom + 0.25))}
                disabled={zoom >= 2}
              >
                <ZoomInIcon />
              </IconButton>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportPDF}
              disabled={exportingPDF || visibleTasks.length === 0}
              size="small"
              sx={{
                color: '#d32f2f',
                borderColor: '#d32f2f',
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              Exporter PDF
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateTask}
              size="small"
            >
              Nouvelle tâche
            </Button>
            {onCreateMilestone && (
              <Button
                variant="outlined"
                startIcon={<FlagIcon />}
                onClick={onCreateMilestone}
                size="small"
                color="secondary"
              >
                Nouveau jalon
              </Button>
            )}
          </Box>
        </Stack>

        {/* Drag & Drop hint */}
        <Alert severity="info" icon={<DragIndicatorIcon />} sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Astuce :</strong> Glissez les barres pour modifier les dates.
            Tirez les bords pour ajuster la durée.
          </Typography>
        </Alert>
      </Paper>

      {/* Gantt Chart */}
      <Paper ref={ganttContainerRef} sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', minHeight: 400, overflowX: 'auto' }}>
          {/* Task list column */}
          <Box
            sx={{
              width: { xs: 160, sm: 220, md: 300 },
              borderRight: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              flexShrink: 0
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                height: 80
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Tâches ({visibleTasks.length})
              </Typography>
            </Box>

            {/* Milestone label in task column */}
            {visibleMilestones.length > 0 && (
              <Box sx={{
                px: 2,
                height: 36,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'action.hover'
              }}>
                <FlagIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                <Typography variant="caption" color="secondary.main" fontWeight="bold">
                  Jalons ({visibleMilestones.length})
                </Typography>
              </Box>
            )}

            <Box>
              {visibleTasks.length > 0 ? (
                visibleTasks.map((task) => (
                  <Box
                    key={task.id}
                    sx={{
                      p: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                    onClick={() => onEditTask(task)}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="medium" noWrap sx={{ mb: 0.5 }}>
                        {task.title}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Chip
                          label={task.priority}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: PRIORITY_COLORS[task.priority],
                            color: 'white'
                          }}
                        />
                        {task.phase && (
                          <Chip
                            label={task.phase}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune tâche avec des dates
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Timeline area */}
          <Box ref={timelineRef} sx={{ flex: 1, overflow: 'auto' }}>
            {/* Timeline header */}
            <Box
              sx={{
                display: 'flex',
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                height: 80,
                position: 'sticky',
                top: 0,
                zIndex: 1
              }}
            >
              {timelineDays.map((day, index) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: dayWidth,
                    width: dayWidth,
                    borderRight: 1,
                    borderColor: 'divider',
                    p: 1,
                    textAlign: 'center',
                    backgroundColor: isToday(day) ? 'primary.light' : 'transparent'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: isToday(day) ? 'bold' : 'normal',
                      color: isToday(day) ? 'primary.main' : 'text.secondary'
                    }}
                  >
                    {format(day, 'EEE', { locale: fr })}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isToday(day) ? 'bold' : 'normal',
                      color: isToday(day) ? 'primary.main' : 'text.primary'
                    }}
                  >
                    {format(day, 'd', { locale: fr })}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Milestone markers overlay */}
            {visibleMilestones.length > 0 && (
              <Box sx={{ position: 'relative', height: 36, borderBottom: 1, borderColor: 'divider', bgcolor: 'purple.50' }}>
                {visibleMilestones.map(m => (
                  <Tooltip
                    key={m.id}
                    title={
                      <Box>
                        <Typography variant="caption" fontWeight="bold">{m.title}</Typography>
                        <Typography variant="caption" display="block">
                          {m.due_date ? format(parseLocalDate(m.due_date), 'dd/MM/yyyy') : ''}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {m.status === 'completed' ? 'Atteint' : 'En attente'}
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      onClick={() => onEditMilestone && onEditMilestone(m)}
                      sx={{
                        position: 'absolute',
                        left: getMilestonePosition(m),
                        top: '50%',
                        transform: 'translate(-50%, -50%) rotate(45deg)',
                        width: 16,
                        height: 16,
                        bgcolor: m.color || '#9c27b0',
                        cursor: 'pointer',
                        opacity: m.status === 'completed' ? 0.5 : 1,
                        '&:hover': { transform: 'translate(-50%, -50%) rotate(45deg) scale(1.3)' }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            )}

            {/* Task bars */}
            <Box sx={{ position: 'relative' }}>
              {visibleTasks.length > 0 ? (
                visibleTasks.map((task) => (
                  <Box
                    key={task.id}
                    sx={{
                      height: 60,
                      borderBottom: 1,
                      borderColor: 'divider',
                      position: 'relative'
                    }}
                  >
                    {/* Grid lines */}
                    <Box sx={{ display: 'flex', height: '100%', position: 'absolute', width: '100%' }}>
                      {timelineDays.map((day, dayIndex) => (
                        <Box
                          key={dayIndex}
                          sx={{
                            minWidth: dayWidth,
                            width: dayWidth,
                            borderRight: 1,
                            borderColor: 'divider',
                            backgroundColor: isToday(day) ? 'primary.50' : 'transparent'
                          }}
                        />
                      ))}
                    </Box>

                    {/* Task bar */}
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="caption" fontWeight="bold">
                            {task.title}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {task.start_date && format(parseLocalDate(task.start_date), 'dd/MM/yyyy')}
                            {' → '}
                            {task.end_date && format(parseLocalDate(task.end_date), 'dd/MM/yyyy')}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Progression: {task.progress}%
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                            Glisser pour déplacer, bords pour redimensionner
                          </Typography>
                        </Box>
                      }
                      arrow
                    >
                      <Box
                        onMouseDown={(e) => handleMouseDown(e, task)}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: 32,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          cursor: dragState.isDragging && dragState.taskId === task.id ? 'move' : 'move',
                          transition: dragState.isDragging && dragState.taskId === task.id ? 'none' : 'all 0.2s',
                          overflow: 'hidden',
                          userSelect: 'none',
                          '&:hover': {
                            filter: 'brightness(1.1)',
                            transform: 'translateY(-50%) scale(1.02)',
                            zIndex: 10
                          },
                          ...getTaskBarStyle(task)
                        }}
                      >
                        {/* Resize handle - Start */}
                        <Box
                          onMouseDown={(e) => handleMouseDown(e, task, 'start')}
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 8,
                            cursor: 'ew-resize',
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        />

                        {/* Progress indicator */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${task.progress}%`,
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transition: 'width 0.3s',
                            pointerEvents: 'none'
                          }}
                        />

                        {/* Task title */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'white',
                            fontWeight: 'medium',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            position: 'relative',
                            zIndex: 1,
                            pointerEvents: 'none'
                          }}
                        >
                          {task.title}
                        </Typography>

                        {/* Resize handle - End */}
                        <Box
                          onMouseDown={(e) => handleMouseDown(e, task, 'end')}
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 8,
                            cursor: 'ew-resize',
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 8, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Aucune tâche à afficher pour cette période
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Créez des tâches avec des dates de début et de fin
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="caption" fontWeight="bold" sx={{ mr: 3 }}>
          Légende:
        </Typography>
        <Stack direction="row" spacing={3} sx={{ display: 'inline-flex' }}>
          {Object.entries(STATUS_COLORS).map(([status, color]) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 12, borderRadius: 0.5, backgroundColor: color }} />
              <Typography variant="caption">
                {status === 'not_started' ? 'À faire' :
                 status === 'in_progress' ? 'En cours' :
                 status === 'completed' ? 'Terminé' : 'Bloqué'}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Tâches non planifiées */}
      {unscheduledTasks.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Tâches non planifiées ({unscheduledTasks.length})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Ces tâches n'ont pas encore de dates — cliquez pour les planifier.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {unscheduledTasks.map(task => (
              <Chip
                key={task.id}
                label={task.title}
                onClick={() => onEditTask(task)}
                sx={{
                  borderLeft: `4px solid ${STATUS_COLORS[task.status] || STATUS_COLORS.not_started}`,
                  borderRadius: 1,
                  mb: 0.5
                }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GanttChartDragDrop;
